/**
 * Evaluators grouped by category, to pick for a run. Against the chosen dataset
 * each one is Ready, Partly mapped (a path only some items have), or Needs
 * mapping — the server decides (`status`, `missing`, `partial`). With `fix` (a
 * new experiment), every row can change its mapping inline: "Map field" makes a
 * Needs-mapping evaluator selectable, "Edit mapping" points any other variable
 * elsewhere for this run. Without it, "Open evaluator" is the way to fix one.
 * Archived evaluators are not offered; a line under the list says how many.
 */

import { useState } from "react";
import { Link } from "react-router";
import { Pill } from "@/components/ui/pill";
import { ArchivedMatches } from "@/features/archive/archived";
import { CATEGORY_LABEL, CATEGORY_ORDER } from "@/features/evaluators/labels";
import type { Evaluator } from "@/features/evaluators/types";
import { EvaluatorFix, type FixProps } from "./evaluator-fix";
import { isFixed, statusOf } from "./evaluator-overrides";

const vars = (names: string[]) => names.map((n) => `{{${n}}}`).join(", ");

function StatusLine({ e, fixed, changed }: { e: Evaluator; fixed: boolean; changed: boolean }) {
  const status = statusOf(e);
  if (changed && status !== "missing")
    return <span className="text-xs text-warn">Mapping changed for this run</span>;
  if (status === "missing")
    return (
      <span className="text-xs text-warn">
        {fixed ? "Mapped for this run" : `Needs mapping${e.missing?.length ? `: ${vars(e.missing)}` : ""}`}
      </span>
    );
  if (status === "partial")
    return (
      <span className="text-xs text-warn">
        {(e.partial ?? []).map((p) => `Partly: ${p.path} in ${p.present_in}/${p.total} items`).join(" · ") ||
          "Partly mapped"}
      </span>
    );
  return (
    <span className="line-clamp-2 text-xs text-muted" title={e.description}>
      <span className="text-success">Ready</span>
      {e.description && ` · ${e.description}`}
    </span>
  );
}

function Row({
  e,
  checked,
  onToggle,
  fix,
}: {
  e: Evaluator;
  checked: boolean;
  onToggle: () => void;
  fix?: FixProps | undefined;
}) {
  const override = fix?.fixes.overrides[e.id];
  const fixed = !!fix && isFixed(e, override);
  const changed = Object.entries(override ?? {}).some(
    ([name, path]) => path.trim() && path !== e.default_mapping[name],
  );
  const [mapping, setMapping] = useState(fixed);
  const needs = statusOf(e) === "missing";
  const off = needs && !fixed;
  const editable = !!fix && e.variables.length > 0;
  return (
    <div className="grid grid-cols-[18px_minmax(0,1fr)_auto] items-start gap-2.5 rounded-[7px] px-2.5 py-2 hover:bg-subtle">
      <input
        id={`ev-${e.id}`}
        type="checkbox"
        className="mt-0.5 size-[15px] accent-ink"
        disabled={off}
        checked={!off && checked}
        onChange={onToggle}
      />
      <span className="flex min-w-0 flex-col gap-0.5">
        <label
          htmlFor={`ev-${e.id}`}
          className={`text-[13px] font-medium ${off ? "cursor-not-allowed text-dim" : "cursor-pointer"}`}
        >
          {e.name}
          {e.version > 1 && <span className="ml-1.5 font-mono text-[11px] text-faint">v{e.version}</span>}
        </label>
        <span className="flex flex-wrap items-center gap-x-2">
          <StatusLine e={e} fixed={fixed} changed={changed} />
          {editable ? (
            <button
              type="button"
              className="link text-xs"
              aria-expanded={mapping}
              onClick={() => setMapping(!mapping)}
            >
              {mapping ? "Hide" : needs && !fixed ? "Map field" : "Edit mapping"}
            </button>
          ) : (
            needs && (
              <Link className="link text-xs" to={`/evaluators/${e.id}`} target="_blank" rel="noreferrer">
                Open evaluator
              </Link>
            )
          )}
        </span>
        {editable && mapping && <EvaluatorFix e={e} fix={fix} />}
      </span>
      <Pill>{e.is_paid ? "Paid" : "Free"}</Pill>
    </div>
  );
}

export function EvaluatorPicker({
  evaluators,
  selected,
  onToggle,
  fix,
}: {
  evaluators: Evaluator[];
  selected: Set<string>;
  onToggle: (id: string) => void;
  fix?: FixProps;
}) {
  const groups = CATEGORY_ORDER.map((c) => ({
    category: c,
    items: evaluators.filter((e) => e.category === c),
  })).filter((g) => g.items.length > 0);

  if (evaluators.length === 0)
    return (
      <p className="rounded-[9px] border border-line px-3 py-4 text-xs text-muted">
        No evaluators yet.{" "}
        <Link className="link" to="/evaluators?new=1" target="_blank" rel="noreferrer">
          Add one
        </Link>{" "}
        on the Evaluators page. <ArchivedMatches kind="evaluator" />
      </p>
    );
  return (
    <div className="rounded-[9px] border border-line p-1">
      {groups.map((g) => (
        <fieldset key={g.category}>
          <legend className="px-2.5 pt-2 pb-0.5 text-[11px] font-semibold tracking-[0.05em] text-faint uppercase">
            {CATEGORY_LABEL[g.category]}
          </legend>
          {g.items.map((e) => (
            <Row key={e.id} e={e} checked={selected.has(e.id)} onToggle={() => onToggle(e.id)} fix={fix} />
          ))}
        </fieldset>
      ))}
      <p className="px-2.5 pt-1 pb-2 empty:hidden">
        <ArchivedMatches kind="evaluator" />
      </p>
    </div>
  );
}
